import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { fetchPlans, fetchBillingAccount, fetchUsage, startCheckout, openPortal } from '../data/billing';
import Icon from '../components/Icons';
import { OrgLobby } from './Deals';
import './Pricing.css';

const fmtUsd = (cents) =>
  cents == null
    ? '—'
    : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

const fmtTokens = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const s = abs >= 1e6 ? `${(abs / 1e6).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`
    : abs >= 1e3 ? `${Math.round(abs / 1e3)}K`
    : String(abs);
  return v < 0 ? `−${s}` : s;
};

// Friendly labels for the metering kinds (shared/billing/usage.js `kind`).
const KIND_LABEL = {
  'doc-agent': 'Drafting agent',
  'brain-ask': 'Ask / research',
  'deal-desk': 'Deal desk',
  'workbook-agent': 'Workbook agent',
  'workbook-audit': 'Workbook audit',
  'voice-agent': 'Voice agent',
  'legal-ai': 'Compliance screens',
  'market-study': 'Market studies',
  ingest: 'Document ingest',
  chat: 'Chat',
};

const SUB_BADGE = {
  active: ['Active', 'b-ok'],
  trialing: ['Trial', 'b-info'],
  trial_expired: ['Trial ended', 'b-warn'],
  past_due: ['Past due', 'b-warn'],
  canceled: ['Canceled', 'b-danger'],
};

// Capability matrix rows, driven by the live entitlement flags so the page
// can never promise a gate the catalog doesn't grant.
const MATRIX = [
  { label: 'Projects, immutable versions, deal points register', always: true },
  { label: 'Governed drafting agent + project brain (Ask)', always: true },
  { label: 'Guest drop links + document ingest', key: 'guestDrop', text: (v) => (v === 'full' ? 'Full' : v === 'limited' ? 'Limited' : null) },
  { label: 'AI compare, compromise & Counsel Ask', key: 'compare' },
  { label: 'Surgical revert (changes manifest)', key: 'surgicalRevert' },
  { label: 'Tracked-changes Word export', key: 'exportRedline' },
  { label: 'Document derive (multi-entity)', key: 'derive' },
  { label: 'Jurisdiction screens', key: 'jurisdiction' },
  { label: 'Market study memos', key: 'marketMemo' },
  { label: 'Voice agent', key: 'voice' },
  { label: 'Immutable hash chain + verification reports', key: 'hashChain' },
  { label: 'Word & PowerPoint add-ins', key: 'wordAddin' },
];

// Plans & usage — license tiers (recurring Stripe subscriptions, monthly or
// annual) plus on-demand token packs (ONE-TIME payments → prepaid credits
// drawn down by actual AI usage per project). Catalog and entitlements are
// live-tunable in config/app `billing.*`; this page displays what the
// backend serves and is explicit about what recurs and what doesn't.
export default function Pricing() {
  const { orgId, org, role } = useOrg();
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const canManage = role === 'admin' || role === 'owner';
  const outcome = params.get('billing'); // success | cancelled
  const pendingPlan = params.get('plan'); // handed off from signup CTAs
  const [interval, setInterval_] = useState(params.get('interval') === 'year' ? 'year' : 'month');
  const annual = interval === 'year';
  // Extra seats per plan (quantity line item on the subscription). The signup
  // handoff can preseed the pending plan's count via ?seats=N.
  const [seatsByPlan, setSeatsByPlan] = useState(() => {
    const preset = Math.max(0, Math.min(500, parseInt(params.get('seats') || '0', 10) || 0));
    return pendingPlan && preset > 0 ? { [pendingPlan]: preset } : {};
  });
  const seatsFor = (key) => seatsByPlan[key] || 0;
  const bumpSeats = (key, delta) =>
    setSeatsByPlan((s) => ({ ...s, [key]: Math.max(0, Math.min(500, (s[key] || 0) + delta)) }));

  useEffect(() => {
    fetchPlans().then(setCatalog).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!orgId) return;
    setAccount(null);
    fetchBillingAccount(orgId).then(setAccount).catch((e) => setError(e.message));
  }, [orgId, outcome]);

  // Usage-by-project ledger. Visibility is a server-side live-config policy
  // (billing.usageVisibility: admins-only by default, optionally members see
  // their own projects) — we always ask; a 403 simply hides the section.
  const [usage, setUsage] = useState(null);
  const [usageMonth, setUsageMonth] = useState('');
  useEffect(() => {
    if (!orgId) return;
    setUsage(null);
    fetchUsage(orgId).then((u) => {
      setUsage(u);
      setUsageMonth(u.months[0]?.month || '');
    }).catch(() => setUsage({ months: [], dealNames: {}, events: [] }));
  }, [orgId]);

  const plans = useMemo(() => {
    const entries = Object.entries(catalog?.plans || {});
    return entries
      .map(([key, p]) => ({ key, ...p }))
      .sort((a, b) => (a.amountCents || 0) - (b.amountCents || 0));
  }, [catalog]);

  const packs = useMemo(() => {
    const entries = Object.entries(catalog?.packs || {});
    return entries
      .map(([key, p]) => ({ key, ...p }))
      .sort((a, b) => (a.amountCents || 0) - (b.amountCents || 0));
  }, [catalog]);

  if (!orgId) return <OrgLobby />;

  const sub = account?.subscription;
  const currentPlanKey = sub && ['active', 'trialing', 'past_due'].includes(sub.status) ? sub.planKey : null;
  const everSubscribed = !!sub?.subscriptionId;
  const [subLabel, subBadge] = SUB_BADGE[sub?.status] || ['Free', 'b-muted'];
  const credits = account?.credits?.tokens ?? 0;
  const monthTokens = account?.usage?.tokens ?? 0;
  const included = account?.entitlements?.includedTokens;

  const buy = async (opts, key) => {
    setError('');
    setBusyKey(key);
    try {
      await startCheckout({ orgId, ...opts });
    } catch (e) {
      setError(e.message);
      setBusyKey('');
    }
  };

  const pending = pendingPlan && !currentPlanKey ? plans.find((p) => p.key === pendingPlan) : null;

  return (
    <div className="cs-page u-stack m-rise fl-pricing" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="fl-pricing-head">
        <h1>Plans &amp; usage</h1>
        <p>
          A recurring license for {org?.name || 'your organization'} covers seats, projects, and a monthly
          AI allowance. One-time token packs cover the busy months — no plan change needed.
        </p>
      </header>

      {outcome === 'success' && (
        <div className="fl-pricing-note ok" role="status">
          Payment received — your plan updates within a few seconds of Stripe’s confirmation.
          <button type="button" className="cs-btn cs-btn-sm" onClick={() => setParams({}, { replace: true })}>Dismiss</button>
        </div>
      )}
      {outcome === 'cancelled' && (
        <div className="fl-pricing-note warn" role="status">
          Checkout cancelled — no charge was made.
          <button type="button" className="cs-btn cs-btn-sm" onClick={() => setParams({}, { replace: true })}>Dismiss</button>
        </div>
      )}
      {error && <div className="fl-pricing-note danger" role="alert">{error}</div>}

      {pending && (
        <div className="fl-pricing-note info" role="status">
          <span>
            Finish setting up <strong>{pending.name}</strong> — {pending.fromPrice ? 'from ' : ''}
            {fmtUsd(annual ? pending.amountCentsAnnual : pending.amountCents)}/month,
            {annual ? ' billed annually' : ' billed monthly'}, cancel anytime
            {seatsFor(pending.key) > 0 ? ` plus ${seatsFor(pending.key)} extra seat${seatsFor(pending.key) === 1 ? '' : 's'}` : ''}
            {pending.trialDays > 0 && !everSubscribed ? ` — starts with a ${pending.trialDays}-day free trial` : ''}.
          </span>
          <button
            type="button"
            className="cs-btn cs-btn-primary cs-btn-sm"
            disabled={!canManage || busyKey === pending.key}
            onClick={() => buy({ planKey: pending.key, interval: annual ? 'year' : 'month', seats: seatsFor(pending.key) }, pending.key)}
          >
            {busyKey === pending.key ? 'Opening checkout…' : 'Start checkout'}
          </button>
        </div>
      )}

      {/* Current plan + usage strip */}
      <section className="cs-card fl-pricing-status">
        <div className="fl-status-cell">
          <span className="fl-status-label">Current plan</span>
          <span className="fl-status-value">
            {sub?.planName || 'Free'} <span className={`cs-badge ${subBadge}`}>{subLabel}</span>
          </span>
          {sub?.source === 'signup-trial' && sub.status === 'trialing' ? (
            <span className="fl-status-sub">
              {account?.trialDaysLeft ?? 0} day{(account?.trialDaysLeft ?? 0) === 1 ? '' : 's'} left — pick a plan to keep these features
            </span>
          ) : sub?.status === 'trial_expired' ? (
            <span className="fl-status-sub">trial ended — choose a plan below to restore full features</span>
          ) : sub?.currentPeriodEnd ? (
            <span className="fl-status-sub">renews {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          ) : null}
        </div>
        <div className="fl-status-cell">
          <span className="fl-status-label">AI usage this month</span>
          <span className="fl-status-value u-tabular">{account ? fmtTokens(monthTokens) : '…'} tokens</span>
          {included != null && included !== true && (
            <span className="fl-status-sub">{fmtTokens(included)} included with plan</span>
          )}
        </div>
        <div className="fl-status-cell">
          <span className="fl-status-label">Prepaid token credits</span>
          <span className={`fl-status-value u-tabular${credits < 0 ? ' fl-neg' : ''}`}>{account ? fmtTokens(credits) : '…'}</span>
          <span className="fl-status-sub">{credits < 0 ? 'overage — top up below' : 'from token packs · never expire'}</span>
        </div>
        {canManage && sub?.customerId && (
          <div className="fl-status-cell fl-status-actions">
            <button
              type="button"
              className="cs-btn cs-btn-outline"
              onClick={() => openPortal(orgId).catch((e) => setError(`Billing portal unavailable: ${e.message}`))}
            >
              Manage billing
            </button>
          </div>
        )}
      </section>

      {/* Usage by project — the admin ledger: where the tokens actually went.
          Every AI pass (drafting, asks, workbook builds, ingest, screens) is
          metered against its project; deleted projects keep their spend. */}
      {usage && usage.months.length > 0 && (() => {
        const m = usage.months.find((x) => x.month === usageMonth) || usage.months[0];
        const dealRows = Object.entries(m.byDeal || {}).sort((a, b) => b[1] - a[1]);
        const maxDeal = dealRows[0]?.[1] || 1;
        // Every org project WITHOUT metered usage this month — zero is real
        // data (or the work predates the ledger), so admins see the full set.
        const metered = new Set(dealRows.map(([id]) => id));
        const unmetered = (usage.projects || [])
          .filter((p) => !metered.has(p.id))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        const nameFor = (id) =>
          usage.dealNames?.[id] ?? (usage.projects || []).find((p) => p.id === id)?.name ?? null;
        const kinds = Object.entries(m.byKind || {}).sort((a, b) => b[1] - a[1]);
        // $ reference at the entry pack's per-token rate — context, not a bill.
        const rate = packs.length && packs[0].tokens ? packs[0].amountCents / packs[0].tokens : null;
        const estUsd = (n) => (rate == null ? null : fmtUsd(n * rate));
        const monthLabel = (k) =>
          new Date(`${k}-15T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        return (
          <section className="cs-card u-stack" style={{ '--gap': 'var(--sp-4)' }}>
            <div className="u-split" style={{ alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0 }}>Usage by project</h2>
                <p className="u-small u-muted" style={{ margin: '4px 0 0' }}>
                  Every AI pass — drafting, asks, workbook builds, ingest, compliance screens — is metered
                  against its project.
                  {usage.scope === 'mine'
                    ? ' Showing your projects only.'
                    : ` ${fmtTokens(account?.usage?.allTime ?? 0)} tokens all time.`}
                  {' '}Full metering began Sep 19, 2026 — work before then predates the ledger,
                  so older projects legitimately show zero.
                </p>
              </div>
              {usage.months.length > 1 && (
                <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
                  {usage.months.map((x) => (
                    <button
                      key={x.month}
                      type="button"
                      className="cs-chip"
                      style={usageMonth === x.month ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
                      onClick={() => setUsageMonth(x.month)}
                    >
                      {monthLabel(x.month)}
                    </button>
                  ))}
                </span>
              )}
            </div>
            <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
              <strong className="u-tabular">{fmtTokens(m.total)} tokens in {monthLabel(m.month)}</strong>
              {rate != null && (
                <span className="u-muted">≈ {estUsd(m.total)} at the {fmtTokens(packs[0].tokens)}-pack rate</span>
              )}
            </div>
            {kinds.length > 0 && (
              <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
                {kinds.map(([k, n]) => (
                  <span key={k} className="cs-chip u-tabular">
                    {KIND_LABEL[k] || k}: {fmtTokens(n)}
                  </span>
                ))}
              </div>
            )}
            <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
              {dealRows.map(([dealId, n]) => {
                const name = usage.dealNames?.[dealId];
                return (
                  <div key={dealId} className="fl-usage-row">
                    <span className="u-small" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name ? (
                        <Link to={`/deals/${dealId}`} style={{ textDecoration: 'none' }}>{name}</Link>
                      ) : (
                        <span className="u-muted">(deleted project)</span>
                      )}
                    </span>
                    <span className="fl-usage-bar" aria-hidden="true">
                      <span style={{ width: `${Math.max(2, Math.round((n / maxDeal) * 100))}%` }} />
                    </span>
                    <span className="u-small u-tabular" style={{ textAlign: 'right' }}>
                      {fmtTokens(n)}{rate != null ? ` · ${estUsd(n)}` : ''}
                    </span>
                  </div>
                );
              })}
              {dealRows.length === 0 && (
                <span className="u-small u-muted">No project-attributed usage this month yet.</span>
              )}
            </div>
            {unmetered.length > 0 && (
              <details>
                <summary className="u-small u-muted" style={{ cursor: 'pointer' }}>
                  {unmetered.length} project{unmetered.length === 1 ? '' : 's'} with no metered AI usage in {monthLabel(m.month)}
                </summary>
                <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                  {unmetered.map((p) => (
                    <Link key={p.id} to={`/deals/${p.id}`} className="cs-chip" style={{ textDecoration: 'none' }}>
                      {p.name}
                    </Link>
                  ))}
                </div>
              </details>
            )}
            {(usage.events || []).length > 0 && (
              <details>
                <summary className="u-small u-muted" style={{ cursor: 'pointer' }}>
                  Recent AI activity — the last {usage.events.length} metered requests
                </summary>
                <div className="u-stack u-small" style={{ '--gap': 'var(--sp-1)', marginTop: 'var(--sp-3)' }}>
                  {usage.events.map((e, i) => (
                    <div key={i} className="u-split" style={{ gap: 'var(--sp-3)' }}>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span className="u-muted u-tabular">
                          {new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                        {' · '}{e.name || 'system'}
                        {' · '}{KIND_LABEL[e.kind] || e.kind}
                        {e.dealId && (
                          <> {' · '}
                            {nameFor(e.dealId)
                              ? <Link to={`/deals/${e.dealId}`} style={{ textDecoration: 'none' }}>{nameFor(e.dealId)}</Link>
                              : <span className="u-muted">(deleted project)</span>}
                          </>
                        )}
                      </span>
                      <span className="u-tabular" style={{ flexShrink: 0 }}>{fmtTokens(e.tokens)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </section>
        );
      })()}

      {/* License tiers */}
      <section>
        <div className="fl-pricing-tier-head">
          <div>
            <h2 className="fl-pricing-h2">License</h2>
            <p className="fl-pricing-sub" style={{ marginBottom: 0 }}>
              Recurring subscription — auto-renews {annual ? 'yearly' : 'monthly'}, cancel anytime from Manage billing.
            </p>
          </div>
          <div className="fl-interval" role="group" aria-label="Billing interval">
            <button type="button" className={annual ? '' : 'on'} onClick={() => setInterval_('month')}>Monthly</button>
            <button type="button" className={annual ? 'on' : ''} onClick={() => setInterval_('year')}>
              Annual <span className="fl-interval-save">save 20%</span>
            </button>
          </div>
        </div>
        {!catalog && <div className="cs-skeleton" style={{ height: 280 }} />}
        <div className="fl-plan-grid">
          {plans.map((p) => {
            const isCurrent = currentPlanKey === p.key;
            const highlight = pendingPlan ? pendingPlan === p.key : p.key === 'team';
            const price = annual ? p.amountCentsAnnual : p.amountCents;
            const configured = annual ? p.configuredAnnual : p.configured;
            const trialing = p.trialDays > 0 && !everSubscribed;
            return (
              <div key={p.key} className={`cs-card fl-plan${highlight ? ' featured' : ''}`}>
                {highlight && <span className="fl-plan-flag">{pendingPlan === p.key ? 'Your pick' : 'Most firms'}</span>}
                <h3>{p.name}</h3>
                {p.tagline && <span className="fl-plan-tagline">{p.tagline}</span>}
                <div className="fl-plan-price">
                  {p.fromPrice && <span className="fl-plan-from">from </span>}
                  {fmtUsd(price)}<span> / month</span>
                </div>
                <span className="fl-plan-billnote">
                  {annual
                    ? `Billed annually — ${fmtUsd((price || 0) * 12)}/year, recurring`
                    : 'Billed monthly, recurring — cancel anytime'}
                </span>
                {trialing && (
                  <span className="fl-plan-trial">
                    <Icon name="sparkle" size={12} /> {p.trialDays}-day free trial — card on file, first charge after the trial
                  </span>
                )}
                <ul className="fl-plan-features">
                  {(p.features || []).map((f) => (
                    <li key={f}><Icon name="sparkle" size={14} /> {f}</li>
                  ))}
                </ul>
                {typeof p.entitlements?.maxSeats === 'number' && p.seatsConfigured && (
                  <div className="fl-plan-seats">
                    <span>
                      {p.entitlements.maxSeats} seat{p.entitlements.maxSeats === 1 ? '' : 's'} included ·
                      extra +{fmtUsd(annual ? p.seatOverageCentsAnnual : p.seatOverageCents)}/seat/mo
                    </span>
                    <div className="fl-stepper" role="group" aria-label={`Extra seats for ${p.name}`}>
                      <button type="button" onClick={() => bumpSeats(p.key, -1)} disabled={seatsFor(p.key) === 0}>−</button>
                      <span className="u-tabular">{seatsFor(p.key)}</span>
                      <button type="button" onClick={() => bumpSeats(p.key, 1)}>+</button>
                    </div>
                  </div>
                )}
                {seatsFor(p.key) > 0 && (
                  <span className="fl-plan-billnote">
                    Est. total {fmtUsd((price || 0) + seatsFor(p.key) * ((annual ? p.seatOverageCentsAnnual : p.seatOverageCents) || 0))}/month
                    with {seatsFor(p.key)} extra seat{seatsFor(p.key) === 1 ? '' : 's'}
                  </span>
                )}
                <button
                  type="button"
                  className={`cs-btn ${highlight ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                  disabled={!canManage || isCurrent || !configured || busyKey === p.key}
                  onClick={() => buy({ planKey: p.key, interval: annual ? 'year' : 'month', seats: seatsFor(p.key) }, p.key)}
                  title={!canManage ? 'Only org owners/admins can manage billing' : undefined}
                >
                  {isCurrent ? 'Current plan' : busyKey === p.key ? 'Opening checkout…' : currentPlanKey ? `Switch to ${p.name}` : trialing ? `Start ${p.trialDays}-day trial` : `Choose ${p.name}`}
                </button>
                {p.fromPrice && (
                  <a className="fl-plan-talk" href="mailto:sales@fast.legal?subject=fast.legal%20Enterprise">
                    Larger or regulated team? Talk to us
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Capability matrix — derived from the live entitlement flags */}
      {plans.length > 0 && (
        <section>
          <h2 className="fl-pricing-h2">What each tier unlocks</h2>
          <div className="fl-matrix-wrap">
            <table className="fl-matrix">
              <thead>
                <tr>
                  <th>Capability</th>
                  {plans.map((p) => <th key={p.key}>{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    {plans.map((p) => {
                      const v = row.always ? true : p.entitlements?.[row.key];
                      const txt = row.text ? row.text(v) : null;
                      return (
                        <td key={p.key} className="fl-matrix-cell">
                          {txt != null ? (
                            <span className={txt === 'Full' ? 'fl-matrix-yes' : 'fl-matrix-part'}>{txt || '—'}</span>
                          ) : v ? (
                            <span className="fl-matrix-yes"><Icon name="check" size={13} strokeWidth={2.4} /></span>
                          ) : (
                            <span className="fl-matrix-no">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Token packs */}
      <section>
        <h2 className="fl-pricing-h2">On-demand usage — token packs</h2>
        <p className="fl-pricing-sub">
          <strong>One-time payments, not subscriptions.</strong> Credits are added to your organization,
          never expire, and are drawn down by actual AI usage — tracked per project, so you can charge
          work back to the deal.
        </p>
        <div className="fl-pack-grid">
          {packs.map((p) => (
            <div key={p.key} className="cs-card fl-pack">
              <span className="cs-badge b-muted fl-pack-badge">One-time</span>
              <div className="fl-pack-tokens">{fmtTokens(p.tokens)}</div>
              <div className="fl-pack-sub">AI tokens · never expire</div>
              <div className="fl-pack-price">{fmtUsd(p.amountCents)}</div>
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                disabled={!canManage || !p.configured || busyKey === p.key}
                onClick={() => buy({ packKey: p.key }, p.key)}
                title={!canManage ? 'Only org owners/admins can manage billing' : undefined}
              >
                {busyKey === p.key ? 'Opening checkout…' : 'Buy once'}
              </button>
            </div>
          ))}
        </div>
      </section>

      {!canManage && (
        <p className="fl-pricing-sub">
          You’re signed in as a member — ask an organization owner or admin to change the plan or buy packs.
        </p>
      )}
    </div>
  );
}
